import authentication from '@feathersjs/authentication-client'
import { feathers } from '@feathersjs/feathers'
import socketio from '@feathersjs/socketio-client'
import { io } from 'socket.io-client'
import { getAPIBaseURL } from 'src/utils/apiBaseUrl'

const meetingInviteEvent = 'sharingcenter-appointment-invites meeting-invite'
const meetingInviteAcceptedEvent = 'sharingcenter-appointment-invites invite-accepted'
const meetingInviteDeclinedEvent = 'sharingcenter-appointment-invites invite-declined'
const presenceUpdatedEvent = 'customer-users presence-updated'
const directCallRequestedEvent = 'sharingcenter-colleagues direct-call-requested'
const directCallAcceptedEvent = 'sharingcenter-colleagues direct-call-accepted'
const directCallDeclinedEvent = 'sharingcenter-colleagues direct-call-declined'
const directCallCancelledEvent = 'sharingcenter-colleagues direct-call-cancelled'
const directCallMeetingCreatedEvent = 'sharingcenter-colleagues direct-call-meeting-created'
const noopStorage = {
  getItem: async () => null,
  removeItem: async () => null,
  setItem: async () => null,
}

let realtimeSocket
let realtimeClient
let currentAccessToken
let realtimeConnectionPromise
const presenceUpdatedListeners = new Set()
const meetingInviteListeners = new Set()
const meetingInviteAcceptedListeners = new Set()
const meetingInviteDeclinedListeners = new Set()
const directCallRequestedListeners = new Set()
const directCallAcceptedListeners = new Set()
const directCallDeclinedListeners = new Set()
const directCallCancelledListeners = new Set()
const directCallMeetingCreatedListeners = new Set()

const handleMeetingInvite = (payload, acknowledge) => {
  const acknowledgementPayload = {
    acknowledgedAt: new Date().toISOString(),
    clientSocketId: realtimeSocket?.id ?? null,
    page: typeof window === 'undefined' ? null : window.location.href,
  }

  console.log('[realtime] meeting invite received', {
    acknowledgementPayload,
    payload,
  })

  if (typeof acknowledge === 'function') {
    acknowledge(acknowledgementPayload)
  }

  notifyListeners(meetingInviteListeners, payload)
}

const handlePresenceUpdated = (payload) => {
  console.log('[realtime] presence updated', payload)

  for (const listener of presenceUpdatedListeners) {
    listener(payload)
  }
}

const notifyListeners = (listeners, payload) => {
  for (const listener of listeners) {
    listener(payload)
  }
}

const handleDirectCallRequested = (payload) => {
  console.log('[realtime] direct call requested', payload)
  notifyListeners(directCallRequestedListeners, payload)
}

const handleMeetingInviteAccepted = (payload) => {
  console.log('[realtime] meeting invite accepted', payload)
  notifyListeners(meetingInviteAcceptedListeners, payload)
}

const handleMeetingInviteDeclined = (payload) => {
  console.log('[realtime] meeting invite declined', payload)
  notifyListeners(meetingInviteDeclinedListeners, payload)
}

const handleDirectCallAccepted = (payload) => {
  console.log('[realtime] direct call accepted', payload)
  notifyListeners(directCallAcceptedListeners, payload)
}

const handleDirectCallDeclined = (payload) => {
  console.log('[realtime] direct call declined', payload)
  notifyListeners(directCallDeclinedListeners, payload)
}

const handleDirectCallCancelled = (payload) => {
  console.log('[realtime] direct call cancelled', payload)
  notifyListeners(directCallCancelledListeners, payload)
}

const handleDirectCallMeetingCreated = (payload) => {
  console.log('[realtime] direct call meeting created', payload)
  notifyListeners(directCallMeetingCreatedListeners, payload)
}

const waitForSocketConnect = (socket) =>
  new Promise((resolve, reject) => {
    if (socket.connected) {
      resolve()
      return
    }

    const cleanup = () => {
      socket.off('connect', onConnect)
      socket.off('connect_error', onError)
      socket.off('disconnect', onDisconnect)
    }
    const onConnect = () => {
      cleanup()
      resolve()
    }
    const onError = (error) => {
      cleanup()
      reject(error)
    }
    const onDisconnect = (reason) => {
      cleanup()
      reject(new Error(`Socket disconnected before connect: ${reason}`))
    }

    socket.once('connect', onConnect)
    socket.once('connect_error', onError)
    socket.once('disconnect', onDisconnect)
    socket.connect()
  })

const createRealtimeClient = () => {
  realtimeSocket = io(getAPIBaseURL(), {
    autoConnect: false,
    transports: ['websocket'],
  })
  realtimeSocket.on('connect', () => {
    console.log('[realtime] socket connected', {
      socketId: realtimeSocket.id,
      transport: realtimeSocket.io.engine.transport.name,
    })
  })
  realtimeSocket.on('disconnect', (reason) => {
    console.log('[realtime] socket disconnected', {
      reason,
      socketId: realtimeSocket.id,
    })
  })
  realtimeSocket.on('connect_error', (error) => {
    console.error('[realtime] socket connect error', error)
  })
  realtimeSocket.io.on('reconnect', async (attempt) => {
    console.log('[realtime] socket reconnected', {
      attempt,
      socketId: realtimeSocket.id,
    })

    await authenticateRealtime().catch((error) => {
      console.error('[realtime] socket re-authentication failed', error)
    })
  })

  realtimeClient = feathers()
  realtimeClient.configure(socketio(realtimeSocket, { timeout: 10000 }))
  realtimeClient.configure(
    authentication({
      storage: noopStorage,
    }),
  )
}

const authenticateRealtime = async () => {
  if (!realtimeClient || !currentAccessToken) {
    return false
  }

  const response = await realtimeClient.authenticate({
    strategy: 'jwt',
    accessToken: currentAccessToken,
  })

  console.log('[realtime] socket authenticated', {
    socketId: realtimeSocket?.id ?? null,
    userId: response?.user?._id ?? response?.user?.id ?? null,
  })

  return response
}

export const connectRealtime = async (accessToken) => {
  if (process.env.FRONTEND_TYPE !== 'Customer' || !accessToken) {
    return false
  }

  if (currentAccessToken === accessToken) {
    if (realtimeSocket?.connected) {
      return true
    }

    if (realtimeConnectionPromise) {
      return realtimeConnectionPromise
    }
  }

  await disconnectRealtime()
  currentAccessToken = accessToken
  createRealtimeClient()
  realtimeSocket.on(meetingInviteEvent, handleMeetingInvite)
  realtimeSocket.on(meetingInviteAcceptedEvent, handleMeetingInviteAccepted)
  realtimeSocket.on(meetingInviteDeclinedEvent, handleMeetingInviteDeclined)
  realtimeSocket.on(presenceUpdatedEvent, handlePresenceUpdated)
  realtimeSocket.on(directCallRequestedEvent, handleDirectCallRequested)
  realtimeSocket.on(directCallAcceptedEvent, handleDirectCallAccepted)
  realtimeSocket.on(directCallDeclinedEvent, handleDirectCallDeclined)
  realtimeSocket.on(directCallCancelledEvent, handleDirectCallCancelled)
  realtimeSocket.on(directCallMeetingCreatedEvent, handleDirectCallMeetingCreated)

  const connectionPromise = (async () => {
    await waitForSocketConnect(realtimeSocket)
    await authenticateRealtime()

    return true
  })()

  realtimeConnectionPromise = connectionPromise

  try {
    return await connectionPromise
  } catch (error) {
    if (realtimeConnectionPromise === connectionPromise) {
      await disconnectRealtime()
    }
    throw error
  } finally {
    if (realtimeConnectionPromise === connectionPromise) {
      realtimeConnectionPromise = undefined
    }
  }
}

export const disconnectRealtime = async () => {
  if (!realtimeClient || !realtimeSocket) {
    currentAccessToken = undefined
    realtimeConnectionPromise = undefined

    return
  }

  realtimeSocket.off(meetingInviteEvent, handleMeetingInvite)
  realtimeSocket.off(meetingInviteAcceptedEvent, handleMeetingInviteAccepted)
  realtimeSocket.off(meetingInviteDeclinedEvent, handleMeetingInviteDeclined)
  realtimeSocket.off(presenceUpdatedEvent, handlePresenceUpdated)
  realtimeSocket.off(directCallRequestedEvent, handleDirectCallRequested)
  realtimeSocket.off(directCallAcceptedEvent, handleDirectCallAccepted)
  realtimeSocket.off(directCallDeclinedEvent, handleDirectCallDeclined)
  realtimeSocket.off(directCallCancelledEvent, handleDirectCallCancelled)
  realtimeSocket.off(directCallMeetingCreatedEvent, handleDirectCallMeetingCreated)
  realtimeSocket.disconnect()
  currentAccessToken = undefined
  realtimeConnectionPromise = undefined
  realtimeClient = undefined
  realtimeSocket = undefined
}

export const getRealtimeSocketId = () => realtimeSocket?.id || null

export const onRealtimePresenceUpdated = (listener) => {
  if (typeof listener !== 'function') {
    return () => {}
  }

  presenceUpdatedListeners.add(listener)

  return () => {
    presenceUpdatedListeners.delete(listener)
  }
}

const addRealtimeListener = (listeners, listener) => {
  if (typeof listener !== 'function') {
    return () => {}
  }

  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export const onRealtimeDirectCallRequested = (listener) => addRealtimeListener(directCallRequestedListeners, listener)
export const onRealtimeDirectCallAccepted = (listener) => addRealtimeListener(directCallAcceptedListeners, listener)
export const onRealtimeDirectCallDeclined = (listener) => addRealtimeListener(directCallDeclinedListeners, listener)
export const onRealtimeDirectCallCancelled = (listener) => addRealtimeListener(directCallCancelledListeners, listener)
export const onRealtimeDirectCallMeetingCreated = (listener) => addRealtimeListener(directCallMeetingCreatedListeners, listener)
export const onRealtimeMeetingInvite = (listener) => addRealtimeListener(meetingInviteListeners, listener)
export const onRealtimeMeetingInviteAccepted = (listener) => addRealtimeListener(meetingInviteAcceptedListeners, listener)
export const onRealtimeMeetingInviteDeclined = (listener) => addRealtimeListener(meetingInviteDeclinedListeners, listener)
